import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Pencil, Plus, Trash2, Globe, RefreshCw, FileBarChart, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { CampaignRow } from "@/components/CampaignRow";
import { TemplateLibrary } from "@/components/TemplateLibrary";
import { HubAssetsPanel } from "@/components/HubAssetsPanel";
import { HubEngagement } from "@/components/HubEngagement";
import { hubUrl, PagesHead } from "@/pages/Dashboard";

export default function ProgramDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [eng, setEng] = useState(null);
  const load = () => {
    api.get(`/programs/${id}/engagement`).then((r) => setEng(r.data)).catch(() => {});
    return api.get(`/programs/${id}`).then((r) => setP(r.data));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (p?.website?.status !== "crawling") return;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [p]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return null;
  const reportUrl = `${window.location.origin}/r/${p.report_token}`;
  const recrawl = async () => { try { await api.post(`/programs/${id}/recrawl`); toast.success("Re-scanning website…"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const rotate = async () => {
    if (!window.confirm("Create a new report link? The old link will stop working.")) return;
    await api.post(`/programs/${id}/report-token/rotate`); toast.success("New report link created"); load();
  };
  const w = p.website;
  const lps = p.pages.filter((c) => !c.hub_only);

  const del = async () => {
    if (!window.confirm(`Delete campaign ${p.code}?`)) return;
    try { await api.delete(`/programs/${id}`); toast.success("Campaign deleted"); nav("/admin"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const btn = "h-10 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm flex items-center gap-2";

  return (
    <div>
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> All campaigns</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 sv-rise">
        <div>
          <span className="font-mono text-xs px-2.5 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-300" data-testid="program-detail-code">{p.code}</span>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-4" data-testid="program-detail-name">{p.name}</h1>
          <p className="text-sm text-slate-400 mt-1">{p.client_name}{p.client_website_url && <> · <a href={p.client_website_url} target="_blank" rel="noreferrer" className="text-indigo-400 hover:text-indigo-300">{p.client_website_url}</a></>}</p>
          {w && (
            <div data-testid="program-website-status" className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[#242C3D] bg-[#141A24]">
              <Globe className="w-3.5 h-3.5 text-cyan-400" />
              {w.status === "crawling" ? "Scanning client website for the chat agent…" : w.status === "ready" ? `Chat agent knows ${w.pages.length} website pages` : `Website scan failed: ${w.error}`}
              {w.status !== "crawling" && <button data-testid="program-recrawl-button" onClick={recrawl} className="text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Re-scan</button>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="program-copy-hub-button" onClick={() => { navigator.clipboard.writeText(hubUrl(p.hub_slug)); toast.success("Hub link copied"); }} className={btn}><Copy className="w-4 h-4" /> Copy hub link</button>
          <button data-testid="program-copy-email-link-button" title="Hub link for email sends: replace {{email}} with your email tool's merge tag"
            onClick={() => { navigator.clipboard.writeText(`${hubUrl(p.hub_slug)}?email={{email}}&utm_source=email&utm_medium=email&utm_campaign=${encodeURIComponent(p.code)}`); toast.success("Email link copied — swap {{email}} for your email tool's email merge tag"); }}
            className={btn}><Mail className="w-4 h-4" /> Copy email link</button>
          <a data-testid="program-open-hub-link" href={`/h/${p.hub_slug}`} target="_blank" rel="noreferrer" className={btn}><ExternalLink className="w-4 h-4" /> Open hub</a>
          <a data-testid="program-client-report-link" href={`/r/${p.report_token}`} target="_blank" rel="noreferrer" className={btn}><FileBarChart className="w-4 h-4" /> Client report</a>
          <button data-testid="program-copy-report-button" onClick={() => { navigator.clipboard.writeText(reportUrl); toast.success("Client report link copied"); }} className={btn}><Copy className="w-4 h-4" /> Report link</button>
          <button data-testid="program-rotate-report-button" onClick={rotate} className={btn} title="Reset report link"><RefreshCw className="w-4 h-4" /></button>
          <Link data-testid="program-edit-button" to={`/admin/programs/${id}/edit`} className={btn}><Pencil className="w-4 h-4" /> Edit</Link>
          <button data-testid="program-delete-button" onClick={del} className={`${btn} hover:text-rose-400`}><Trash2 className="w-4 h-4" /></button>
          <Link data-testid="program-new-page-button" to={`/admin/campaigns/new?program=${id}`} className="h-10 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium flex items-center gap-2"><Plus className="w-4 h-4" /> New landing page</Link>
        </div>
      </div>

      <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/60 mb-12 sv-rise sv-d1" data-testid="program-pages">
        <div className="px-6 py-4 border-b border-[#242C3D] flex items-center justify-between">
          <h2 className="font-display font-semibold text-lg">Landing pages & content</h2>
          <span className="font-mono text-xs text-slate-500">{lps.length}</span>
        </div>
        {lps.length === 0 ? (
          <p className="py-14 text-center text-sm text-slate-500" data-testid="program-pages-empty">No landing pages yet — create one or start from a template below.</p>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><PagesHead /><tbody>{lps.map((c) => <CampaignRow key={c.id} c={c} onChanged={load} />)}</tbody></table></div>
        )}
      </div>
      <HubAssetsPanel pid={id} onChanged={load} />
      <HubEngagement data={eng} />
      <TemplateLibrary programId={id} />
    </div>
  );
}
